import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ApiService from "../services/ApiService";

const schema = Yup.object({
  email: Yup.string().trim().email("Enter a valid email").required("Email is required"),
  mobile: Yup.string().trim().test("mobile-format", "Use digits and optional +, spaces, or parentheses",
    (value) => !value || /^[\d+\-\s()]+$/.test(value)),
  displayName: Yup.string().trim().min(1, "Display name is required").max(120).required("Display name is required"),
  password: Yup.string().min(8, "At least 8 characters").required("Password is required"),
  confirmPassword: Yup.string().oneOf([Yup.ref("password")], "Passwords must match").required("Confirm your password"),
});

const PERKS = [
  "Connect Facebook, Instagram & Google",
  "Campaign-level performance breakdown",
  "Export reports as CSV or PDF",
  "No credit card required to start",
];

export function SignUpPage() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Left dark panel */}
      <Box sx={{
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        justifyContent: "space-between",
        width: 460, flexShrink: 0,
        bgcolor: "#0F172A", p: 5,
        position: "relative", overflow: "hidden",
      }}>
        <Box sx={{ position: "absolute", top: -100, right: -100, width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(91,91,214,0.3) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: -80, left: -80, width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle, rgba(14,165,233,0.18) 0%, transparent 70%)", pointerEvents: "none" }} />

        {/* Brand */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, position: "relative" }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: "linear-gradient(135deg, #818CF8 0%, #5B5BD6 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(91,91,214,0.5)" }}>
            <BarChartOutlinedIcon sx={{ fontSize: 20, color: "#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#F1F5F9", lineHeight: 1.2 }}>OrkaAnalytics</Typography>
            <Typography sx={{ fontSize: "0.65rem", color: "#475569", letterSpacing: "0.04em" }}>Marketing platform</Typography>
          </Box>
        </Box>

        {/* Value props */}
        <Box sx={{ position: "relative" }}>
          <Typography sx={{ fontSize: "1.875rem", fontWeight: 800, color: "#F1F5F9", lineHeight: 1.25, letterSpacing: "-0.03em", mb: 2 }}>
            Start making{" "}
            <Box component="span" sx={{ background: "linear-gradient(90deg, #818CF8 0%, #38BDF8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              smarter decisions.
            </Box>
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "#64748B", lineHeight: 1.75, mb: 4 }}>
            Everything you need to understand your marketing performance — set up in minutes.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {PERKS.map((perk) => (
              <Box key={perk} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#818CF8", flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.875rem", color: "#94A3B8" }}>{perk}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Bottom note */}
        <Box sx={{ bgcolor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 3, p: 3 }}>
          <Typography sx={{ fontSize: "0.8rem", color: "#475569" }}>
            Already have an account?{" "}
            <Link component={RouterLink} to="/signin" sx={{ color: "#818CF8", textDecoration: "none", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>
              Sign in →
            </Link>
          </Typography>
        </Box>
      </Box>

      {/* Right form panel */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "#F0F2F7", p: { xs: 3, sm: 5 } }}>
        {/* Mobile brand */}
        <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1.5, mb: 4 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "9px", background: "linear-gradient(135deg, #818CF8 0%, #5B5BD6 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <BarChartOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#0F172A" }}>OrkaAnalytics</Typography>
        </Box>

        <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #E8EDF5", p: { xs: 3, sm: 4.5 }, width: "100%", maxWidth: 440, boxShadow: "0px 4px 24px rgba(15,23,42,0.07)" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.4rem", color: "#0F172A", mb: 0.75 }}>Create your account</Typography>
          <Typography variant="body2" sx={{ color: "#64748B", mb: 3 }}>Free forever · No credit card required</Typography>

          {error && <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>{error}</Alert>}

          <Formik
            initialValues={{ email: "", mobile: "", displayName: "", password: "", confirmPassword: "" }}
            validationSchema={schema}
            onSubmit={async (values, { setSubmitting }) => {
              setError(null);
              try {
                const mobile = values.mobile.trim();
                await ApiService.signup({
                  email: values.email.trim(),
                  displayName: values.displayName.trim(),
                  password: values.password,
                  ...(mobile ? { mobile } : {}),
                });
                navigate("/", { replace: true });
              } catch (e) {
                setError(e?.response?.data?.message || e?.message || "Sign up failed. Try again.");
              } finally { setSubmitting(false); }
            }}
          >
            {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
              <Form noValidate>
                <TextField fullWidth name="displayName" label="Display name" margin="normal"
                  value={values.displayName} onChange={handleChange} onBlur={handleBlur}
                  error={touched.displayName && Boolean(errors.displayName)} helperText={touched.displayName && errors.displayName}
                  autoComplete="name" autoFocus />
                <TextField fullWidth name="email" label="Email address" type="email" margin="normal"
                  value={values.email} onChange={handleChange} onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)} helperText={touched.email && errors.email}
                  autoComplete="email" />
                <TextField fullWidth name="mobile" label="Mobile number (optional)" margin="normal"
                  value={values.mobile} onChange={handleChange} onBlur={handleBlur}
                  error={touched.mobile && Boolean(errors.mobile)} helperText={touched.mobile && errors.mobile}
                  autoComplete="tel" />
                <TextField fullWidth name="password" label="Password" type={showPassword ? "text" : "password"} margin="normal"
                  value={values.password} onChange={handleChange} onBlur={handleBlur}
                  error={touched.password && Boolean(errors.password)} helperText={touched.password && errors.password}
                  autoComplete="new-password"
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end" tabIndex={-1}>
                        {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )}} />
                <TextField fullWidth name="confirmPassword" label="Confirm password" type={showConfirm ? "text" : "password"} margin="normal"
                  value={values.confirmPassword} onChange={handleChange} onBlur={handleBlur}
                  error={touched.confirmPassword && Boolean(errors.confirmPassword)} helperText={touched.confirmPassword && errors.confirmPassword}
                  autoComplete="new-password"
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowConfirm((v) => !v)} edge="end" tabIndex={-1}>
                        {showConfirm ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  )}} />
                <Button fullWidth type="submit" variant="contained" size="large" disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{ mt: 3, py: 1.25, fontSize: "0.9375rem" }}>
                  {isSubmitting ? "Creating account…" : "Create account"}
                </Button>
              </Form>
            )}
          </Formik>

          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" sx={{ color: "#94A3B8", px: 1 }}>Already have an account?</Typography>
          </Divider>
          <Button fullWidth variant="outlined" component={RouterLink} to="/signin" sx={{ py: 1.125 }}>
            Sign in instead
          </Button>
        </Box>

        <Typography variant="caption" sx={{ color: "#94A3B8", mt: 4 }}>
          © {new Date().getFullYear()} OrkaAnalytics · All rights reserved
        </Typography>
      </Box>
    </Box>
  );
}
